import { decodeProtectedHeader, importJWK, jwtVerify } from "jose";

/**
 * Verifies a Tiltify-issued JWS token and returns the decoded payload.
 * The public key is embedded in the JWT's protected header as a JWK.
 *
 * @param {string} jwt - The compact JWS string sent from the Tiltify Lambda.
 * @returns {Promise<{user: Object, profile: Object, tokens: Object}>}
 */
function verifyTiltifyJWT(jwt) {
  const headers = decodeProtectedHeader(jwt);
  const publicKey = headers.jwk;
  if (!publicKey) {
    throw new Error("Tiltify JWT verification failed: JWK is missing from header");
  }
  return importJWK(publicKey, "RS256")
    .then((key) => jwtVerify(jwt, key))
    .then((result) => result.payload);
}

/**
 * Opens a popup window to start the Tiltify OAuth flow.
 * On success, calls `setUser` with `{ ...user, tokens }` where tokens contains
 * `accessToken`, `refreshToken`, `expiresIn`, and `createdAt`.
 *
 * @param {function({id: string, username: string, slug: string, tokens: Object}): void} setUser
 */
export function openTiltifyOAuthPopup(setUser = (_user) => {}) {
  const url = new URL("https://v5api.tiltify.com/oauth/authorize");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", import.meta.env.VITE_TILTIFY_CLIENT_ID);
  url.searchParams.set("redirect_uri", encodeURI(import.meta.env.VITE_TILTIFY_REDIRECT_URI));
  url.searchParams.set("scope", "public");

  const origin = new URL(import.meta.env.VITE_TILTIFY_REDIRECT_URI).origin;

  const win = window.open(url.toString());
  win.focus();

  const handleEvent = (event) => {
    if (event.origin !== origin) {
      return;
    }

    try {
      // Legacy path: Lambda sent a plain JSON object with an `id` field.
      const data = JSON.parse(event.data);
      if (data.id) {
        setUser(data);
        window.removeEventListener("message", handleEvent);
      }
    } catch (_err) {
      // Current path: Lambda sent a signed JWT.
      verifyTiltifyJWT(event.data)
        .then((payload) => {
          setUser({ ...payload.user, tokens: payload.tokens });
          window.removeEventListener("message", handleEvent);
        })
        .catch((err) => {
          // biome-ignore lint/suspicious/noConsole: error logging for JWT verification failures
          console.error("Failed to verify Tiltify JWT:", err);
        });
    }
  };

  window.addEventListener("message", handleEvent);
}

/**
 * Returns `true` when the Tiltify access token has expired or will expire within
 * the given buffer window.
 *
 * @param {string|null|undefined} createdAt   - ISO-8601 timestamp from `tokens.createdAt`
 * @param {string|number|null|undefined} expiresIn - Lifetime in seconds from `tokens.expiresIn`
 * @param {number} [bufferSeconds=300]        - Seconds before actual expiry to treat as expired
 * @returns {boolean}
 */
export function isTiltifyTokenExpired(createdAt, expiresIn, bufferSeconds = 300) {
  if (!createdAt || !expiresIn) {
    // Can't determine expiry — assume expired so a refresh is attempted.
    return true;
  }
  const expiresAt = new Date(createdAt).getTime() + Number(expiresIn) * 1000;
  return Date.now() >= expiresAt - bufferSeconds * 1000;
}

/**
 * Calls the Tiltify Lambda refresh endpoint to exchange a refresh token for a
 * new access token.  The refresh URL is derived from `VITE_TILTIFY_REDIRECT_URI`
 * by replacing the last path segment (`getUserId`) with `refreshToken`.
 *
 * Resolves with `{ ...user, tokens }` on success, or `null` when the refresh
 * fails (network error, invalid refresh token, unexpected response shape, etc.).
 *
 * @param {string} refreshToken
 * @returns {Promise<{id: string, username: string, slug: string, tokens: Object}|null>}
 */
export function refreshTiltifyToken(refreshToken) {
  // Derive: https://.../api/tiltify/getUserId  →  https://.../api/tiltify/refreshToken
  const refreshUrl = import.meta.env.VITE_TILTIFY_REDIRECT_URI.replace(/\/[^/]+$/, "/refreshToken");

  return fetch(refreshUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  })
    .then((res) => {
      if (!res.ok) {
        return null;
      }
      return res.json();
    })
    .then((data) => {
      if (!data?.jwt) {
        return null;
      }
      return verifyTiltifyJWT(data.jwt).then((payload) => ({
        ...payload.user,
        tokens: payload.tokens,
      }));
    })
    .catch((err) => {
      // biome-ignore lint/suspicious/noConsole: error logging for token refresh failures
      console.error("Tiltify token refresh failed:", err);
      return null;
    });
}
